import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Palette,
  ArrowLeft,
  Upload,
  Image,
  Globe,
  Mail,
  Phone,
  Loader2,
  Eye,
  Save,
  RefreshCw,
  Check,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_PRESETS = [
  { name: 'Indigo', primary: '#4F46E5', secondary: '#7C3AED', accent: '#10B981' },
  { name: 'Blue', primary: '#2563EB', secondary: '#3B82F6', accent: '#F59E0B' },
  { name: 'Emerald', primary: '#059669', secondary: '#10B981', accent: '#6366F1' },
  { name: 'Rose', primary: '#E11D48', secondary: '#F43F5E', accent: '#8B5CF6' },
  { name: 'Slate', primary: '#475569', secondary: '#64748B', accent: '#F97316' },
  { name: 'Amber', primary: '#D97706', secondary: '#F59E0B', accent: '#3B82F6' },
];

export default function WhiteLabelBranding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState(null);
  const [agentId, setAgentId] = useState(null);
  
  const [form, setForm] = useState({
    company_name: '',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#10B981',
    subdomain: '',
    support_email: '',
    support_phone: '',
    custom_footer_text: '',
    hide_powered_by: false,
  });

  const token = localStorage.getItem('session_token');

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    setLoading(true);
    try {
      // First get agent ID
      const agentRes = await fetch(`${API_URL}/api/managing-agents/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (agentRes.ok) {
        const agentData = await agentRes.json();
        setAgentId(agentData.agent_id);
        
        // Then fetch branding
        const brandingRes = await fetch(`${API_URL}/api/agent-features/branding/${agentData.agent_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (brandingRes.ok) {
          const brandingData = await brandingRes.json();
          setBranding(brandingData);
          if (!brandingData.is_default) {
            setForm({
              company_name: brandingData.company_name || '',
              primary_color: brandingData.primary_color || '#4F46E5',
              secondary_color: brandingData.secondary_color || '#7C3AED',
              accent_color: brandingData.accent_color || '#10B981',
              subdomain: brandingData.subdomain || '',
              support_email: brandingData.support_email || '',
              support_phone: brandingData.support_phone || '',
              custom_footer_text: brandingData.custom_footer_text || '',
              hide_powered_by: brandingData.hide_powered_by || false,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBranding = async () => {
    if (!agentId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/agent-features/branding/create?agent_id=${agentId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Branding profile created');
        fetchBranding();
      }
    } catch (error) {
      toast.error('Failed to create branding profile');
    }
  };

  const handleSaveBranding = async () => {
    if (!agentId) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/agent-features/branding/${agentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      
      if (res.ok) {
        toast.success('Branding saved successfully');
        fetchBranding();
      }
    } catch (error) {
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, logoType) => {
    const file = e.target.files?.[0];
    if (!file || !agentId) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(
        `${API_URL}/api/agent-features/branding/${agentId}/logo?logo_type=${logoType}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('Logo uploaded successfully');
        fetchBranding();
      }
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const applyColorPreset = (preset) => {
    setForm({
      ...form,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="white-label-branding">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">White Label Branding</h1>
              <p className="text-slate-500 mt-1">Customize your portal with your brand identity</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchBranding}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              {!branding?.is_default ? (
                <Button onClick={handleSaveBranding} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              ) : (
                <Button onClick={handleCreateBranding}>
                  <Palette className="w-4 h-4 mr-2" />
                  Setup Branding
                </Button>
              )}
            </div>
          </div>
        </div>

        {branding?.is_default ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Palette className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No Branding Configured</h3>
              <p className="text-slate-500 mb-4">Setup your white label branding to customize the portal for your clients.</p>
              <Button onClick={handleCreateBranding}>
                <Palette className="w-4 h-4 mr-2" />
                Setup Branding
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Settings */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Company Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={form.company_name}
                      onChange={(e) => setForm({...form, company_name: e.target.value})}
                      placeholder="Your Company Name"
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Support Email</Label>
                      <Input
                        value={form.support_email}
                        onChange={(e) => setForm({...form, support_email: e.target.value})}
                        placeholder="support@company.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Support Phone</Label>
                      <Input
                        value={form.support_phone}
                        onChange={(e) => setForm({...form, support_phone: e.target.value})}
                        placeholder="+27 11 123 4567"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Logo Upload */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Logo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label className="mb-2 block">Primary Logo</Label>
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                        {branding?.logo_url ? (
                          <img 
                            src={`${API_URL}${branding.logo_url}`} 
                            alt="Logo" 
                            className="max-h-16 mx-auto mb-2"
                          />
                        ) : (
                          <Image className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleLogoUpload(e, 'primary')}
                          className="hidden"
                          id="logo-upload"
                        />
                        <label htmlFor="logo-upload">
                          <Button variant="outline" size="sm" asChild>
                            <span className="cursor-pointer">
                              <Upload className="w-4 h-4 mr-1" />
                              Upload Logo
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label className="mb-2 block">Favicon</Label>
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                        {branding?.favicon_url ? (
                          <img 
                            src={`${API_URL}${branding.favicon_url}`} 
                            alt="Favicon" 
                            className="w-8 h-8 mx-auto mb-2"
                          />
                        ) : (
                          <Globe className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleLogoUpload(e, 'favicon')}
                          className="hidden"
                          id="favicon-upload"
                        />
                        <label htmlFor="favicon-upload">
                          <Button variant="outline" size="sm" asChild>
                            <span className="cursor-pointer">
                              <Upload className="w-4 h-4 mr-1" />
                              Upload
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Colors */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Brand Colors</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Color Presets */}
                  <div>
                    <Label className="mb-2 block">Quick Presets</Label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_PRESETS.map(preset => (
                        <button
                          key={preset.name}
                          onClick={() => applyColorPreset(preset)}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border hover:border-slate-400 transition-colors"
                        >
                          <div className="flex -space-x-1">
                            <div className="w-4 h-4 rounded-full border-2 border-white" style={{ backgroundColor: preset.primary }} />
                            <div className="w-4 h-4 rounded-full border-2 border-white" style={{ backgroundColor: preset.secondary }} />
                            <div className="w-4 h-4 rounded-full border-2 border-white" style={{ backgroundColor: preset.accent }} />
                          </div>
                          <span className="text-sm">{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Custom Colors */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Primary Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={form.primary_color}
                          onChange={(e) => setForm({...form, primary_color: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={form.primary_color}
                          onChange={(e) => setForm({...form, primary_color: e.target.value})}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Secondary Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={form.secondary_color}
                          onChange={(e) => setForm({...form, secondary_color: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={form.secondary_color}
                          onChange={(e) => setForm({...form, secondary_color: e.target.value})}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Accent Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={form.accent_color}
                          onChange={(e) => setForm({...form, accent_color: e.target.value})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={form.accent_color}
                          onChange={(e) => setForm({...form, accent_color: e.target.value})}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Domain */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Custom Domain</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Subdomain</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={form.subdomain}
                        onChange={(e) => setForm({...form, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                        placeholder="yourcompany"
                        className="flex-1"
                      />
                      <span className="text-slate-500">.bodycorp365.com</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Your portal will be accessible at: {form.subdomain || 'yourcompany'}.bodycorp365.com
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <strong>Custom Domain:</strong> For a fully custom domain (e.g., portal.yourcompany.co.za), 
                      please contact support to configure DNS settings.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Footer */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Footer Customization</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Custom Footer Text</Label>
                    <Input
                      value={form.custom_footer_text}
                      onChange={(e) => setForm({...form, custom_footer_text: e.target.value})}
                      placeholder="© 2026 Your Company. All rights reserved."
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="hide-powered-by"
                      checked={form.hide_powered_by}
                      onChange={(e) => setForm({...form, hide_powered_by: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="hide-powered-by" className="cursor-pointer">
                      Hide "Powered by BodyCorp365" branding
                    </Label>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Preview */}
            <div className="lg:col-span-1">
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Mini Preview */}
                  <div className="border rounded-lg overflow-hidden">
                    {/* Header Preview */}
                    <div 
                      className="p-3 text-white"
                      style={{ backgroundColor: form.primary_color }}
                    >
                      <div className="flex items-center gap-2">
                        {branding?.logo_url ? (
                          <img src={`${API_URL}${branding.logo_url}`} alt="Logo" className="h-6" />
                        ) : (
                          <div className="w-6 h-6 bg-white/20 rounded" />
                        )}
                        <span className="font-semibold text-sm">{form.company_name || 'Your Company'}</span>
                      </div>
                    </div>
                    
                    {/* Content Preview */}
                    <div className="p-4 bg-slate-50">
                      <div className="space-y-2">
                        <div 
                          className="h-8 rounded text-white text-xs flex items-center justify-center"
                          style={{ backgroundColor: form.primary_color }}
                        >
                          Primary Button
                        </div>
                        <div 
                          className="h-8 rounded text-white text-xs flex items-center justify-center"
                          style={{ backgroundColor: form.secondary_color }}
                        >
                          Secondary Button
                        </div>
                        <div 
                          className="h-2 rounded"
                          style={{ backgroundColor: form.accent_color }}
                        />
                      </div>
                    </div>
                    
                    {/* Footer Preview */}
                    <div className="p-2 bg-slate-100 text-center">
                      <p className="text-xs text-slate-500">
                        {form.custom_footer_text || '© 2026 Your Company'}
                      </p>
                      {!form.hide_powered_by && (
                        <p className="text-xs text-slate-400">Powered by BodyCorp365</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Domain Preview */}
                  <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                    <Label className="text-xs text-slate-500">Portal URL</Label>
                    <p className="text-sm font-mono">
                      {form.subdomain ? `${form.subdomain}.bodycorp365.com` : 'yourcompany.bodycorp365.com'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
